import { useCallback, useEffect, useRef, useState } from 'react';
import {
  acceptLegalDocument,
  getPendingReacceptance,
} from '@powerful/data';
import type {
  LegalClient,
  LegalDocument,
  LegalLocale,
  LegalRole,
} from '@powerful/data';
import { resolveLocale } from '@powerful/config/i18n';

export interface UseLegalReacceptanceInput {
  client: LegalClient;
  userId: string | null;
  locale: string;
  role: LegalRole | null;
  now?: Date;
}

export interface UseLegalReacceptanceResult {
  pending: LegalDocument[];
  loading: boolean;
  error: Error | null;
  acceptingDocumentId: string | null;
  reload: () => Promise<void>;
  accept: (documentId: string) => Promise<void>;
}

function asError(value: unknown): Error {
  return value instanceof Error ? value : new Error('legal_reacceptance_failed');
}

/**
 * Coordena pendência e re-aceite sem decidir regras de negócio na UI. Em qualquer falha de
 * consulta ou confirmação, `pending` fica vazio e `error` é exposto: o guard pode manter a rota
 * bloqueada e oferecer retry, em vez de liberar uma sessão sem prova.
 */
export function useLegalReacceptance({
  client,
  userId,
  locale: rawLocale,
  role,
  now,
}: UseLegalReacceptanceInput): UseLegalReacceptanceResult {
  const locale = resolveLocale(rawLocale) as LegalLocale;
  // T1.19 (/code-review): o resultado fica associado à identidade para a qual foi calculado. Duas
  // falhas eram possíveis sem isto — ambas abriam o gate que bloqueia TODAS as rotas privadas:
  //  1. `loading` só nascia `true` na montagem; quando a identidade chegava depois, o render antes
  //     do efeito dizia "nada pendente, sem carregar" e o shell privado aparecia por um frame;
  //  2. uma consulta mais lenta de outro idioma/papel/utilizador resolvia por último e sobrescrevia
  //     o estado atual (podia até devolver [] com termos por aceitar).
  const chave = userId && role ? `${userId}|${role}|${locale}` : null;
  const [resultado, setResultado] = useState<{
    chave: string | null;
    pending: LegalDocument[];
    error: Error | null;
  }>({ chave: null, pending: [], error: null });
  const [emCurso, setEmCurso] = useState(false);
  const [acceptingDocumentId, setAcceptingDocumentId] = useState<string | null>(null);
  const sequencia = useRef(0);

  const reload = useCallback(async () => {
    // Cada consulta recebe um número; só a mais recente pode escrever o estado.
    const minha = ++sequencia.current;

    if (!userId || !role) {
      setResultado({ chave: null, pending: [], error: null });
      setEmCurso(false);
      return;
    }

    const chaveDaConsulta = `${userId}|${role}|${locale}`;
    setEmCurso(true);
    try {
      const result = await getPendingReacceptance(client, userId, locale, role, now ?? new Date());
      if (minha !== sequencia.current) return;
      setResultado({ chave: chaveDaConsulta, pending: result, error: null });
    } catch (cause) {
      if (minha !== sequencia.current) return;
      setResultado({ chave: chaveDaConsulta, pending: [], error: asError(cause) });
    } finally {
      if (minha === sequencia.current) setEmCurso(false);
    }
  }, [client, locale, now, role, userId]);

  // Derivado NO RENDER: enquanto o resultado guardado não for o da identidade atual, está a carregar
  // — o gate bloqueia no próprio render em que a identidade chega, sem esperar pelo efeito.
  const atual = resultado.chave !== null && resultado.chave === chave;
  const loading = chave !== null && (!atual || emCurso);
  const pending = atual ? resultado.pending : [];
  const error = atual ? resultado.error : null;

  useEffect(() => {
    void reload();
  }, [reload]);

  const accept = useCallback(
    async (documentId: string) => {
      if (!userId || !role) return;
      setAcceptingDocumentId(documentId);
      setResultado((anterior) => ({ ...anterior, error: null }));
      try {
        await acceptLegalDocument(client, documentId, 'reacceptance_prompt');
        // A confirmação só libera o guard depois de consultar o estado derivado do servidor.
        await reload();
      } catch (cause) {
        const failure = asError(cause);
        setResultado((anterior) => ({ ...anterior, error: failure }));
        throw failure;
      } finally {
        setAcceptingDocumentId(null);
      }
    },
    [client, reload, role, userId],
  );

  return { pending, loading, error, acceptingDocumentId, reload, accept };
}
